import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { ProductList } from "src/app/features/Product/types/ProductList";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayEnumValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const ProductsTable: FC<ResourceTableProps<ProductList>> = ({ onStateChanged, onRowSelectionChange, datasource, resource }) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<ProductList>): ExtendedColumnsType<ProductList>[] => {
            return [
                {
                    title: t("product.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("product.code"),
                    key: "grCode",
                    dataIndex: "grCode",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "grCode" ? sortInfo.order : null,
                },
                {
                    title: t("product.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("product.shortName"),
                    key: "shortName",
                    dataIndex: "shortName",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "shortName" ? sortInfo.order : null,
                },
                {
                    title: t("product.type"),
                    key: "type",
                    dataIndex: "type",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "type" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "product-type"),
                },
                {
                    title: t("product.vatCode"),
                    key: "vatCode",
                    dataIndex: "vatCode",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "vatCode" ? sortInfo.order : null,
                },
                {
                    title: t("product.itemType"),
                    key: "itemType",
                    dataIndex: "itemType",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "itemType" ? sortInfo.order : null,
                    render: (value) => displayEnumValue(value, "product-item-type"),
                },
                {
                    title: t("product.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("product.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: ProductList) => {
        navigate(`${BASE_PATH}/${ResourceName.PRODUCTS}/${FORM_ACTION.EDIT}/${record.id}`);
    };

    return (
        (datasource && (
            <GenericTable<ProductList>
                name="products_table"
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: ProductList) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
